import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { getCurrencySymbol } from '../lib/currency';

import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import {
  Clock, DollarSign, Target, Shield, AlertTriangle, Lightbulb,
  Building2, Users, Layers, BarChart3, ChevronDown, ChevronUp,
  Lock, CalendarDays, Eye
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function MetricCard({ icon: Icon, label, value, sub, color = '#4ABFCF' }) {
  return (
    <div className="p-4 rounded-xl bg-white border border-slate-100 shadow-sm" data-testid={`metric-${label.toLowerCase().replace(/\s/g, '-')}`}>
      <div className="flex items-center gap-2 mb-1">
        <Icon className="w-4 h-4" style={{ color }} />
        <span className="text-xs text-slate-500 font-medium">{label}</span>
      </div>
      <div className="text-2xl font-bold text-[#0F2040] font-['Outfit']">{value}</div>
      {sub && <div className="text-xs text-slate-400 mt-0.5">{sub}</div>}
    </div>
  );
}

export default function SharedEstimatePage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedGroups, setExpandedGroups] = useState({});

  useEffect(() => {
    fetchSharedEstimate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const fetchSharedEstimate = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/shared/${token}`);
      setData(res.data);
    } catch (err) {
      const status = err.response?.status;
      if (status === 404) setError('This share link was not found.');
      else if (status === 410) setError(err.response?.data?.detail || 'This share link has expired or been revoked.');
      else setError('Unable to load the shared estimate.');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-[#4ABFCF] border-t-transparent rounded-full animate-spin mx-auto" />
          <p className="mt-4 text-slate-500">Loading shared estimate...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center" data-testid="shared-error">
        <Card className="max-w-md w-full mx-4">
          <CardContent className="p-10 text-center">
            <div className="w-14 h-14 rounded-full bg-red-50 flex items-center justify-center mx-auto mb-4">
              <Lock className="w-7 h-7 text-red-400" />
            </div>
            <h2 className="text-lg font-semibold text-[#0F2040] mb-2">Link Unavailable</h2>
            <p className="text-sm text-slate-500 mb-6">{error}</p>
            <Button variant="outline" onClick={() => navigate('/')} data-testid="shared-error-home-btn">
              Go to Home
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const est = data.estimate;
  const sym = getCurrencySymbol(est?.currency_code);
  const results = est.results || {};
  const branding = data.branding;
  const primaryColor = branding?.primary_color || '#0F2040';
  const vis = data.visibility || {};

  const components = results.component_breakdown || [];
  const groups = {};
  components.forEach(c => {
    const g = c.group || 'Other';
    if (!groups[g]) groups[g] = [];
    groups[g].push(c);
  });

  const toggleGroup = (g) => setExpandedGroups(prev => ({ ...prev, [g]: !prev[g] }));

  const getComplexityColor = (label) => {
    switch (label) {
      case 'Simple': return 'bg-green-100 text-green-700';
      case 'Moderate': return 'bg-blue-100 text-blue-700';
      case 'Complex': return 'bg-amber-100 text-amber-700';
      case 'Very Complex': return 'bg-red-100 text-red-700';
      default: return 'bg-slate-100 text-slate-700';
    }
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="shared-estimate-page">
      {/* Header */}
      <div className="bg-[#0F2040] text-white" style={{ backgroundColor: primaryColor }}>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center gap-2 mb-1">
            <Eye className="w-4 h-4 text-[#4ABFCF]" />
            <span className="text-xs text-slate-300">Shared Estimate (Read-Only)</span>
            {data.shared_by && <span className="text-xs text-slate-400 ml-1">by {data.shared_by}</span>}
          </div>

          <h1 className="text-2xl sm:text-3xl font-bold font-['Outfit'] mt-2" data-testid="shared-estimate-name">
            {est.estimate_name}
          </h1>

          <div className="flex flex-wrap items-center gap-3 mt-3 text-sm">
            {est.organization_name && (
              <span className="flex items-center gap-1 text-slate-300">
                <Building2 className="w-4 h-4" /> {est.organization_name}
              </span>
            )}
            {est.implementation_type && (
              <Badge className="bg-white/10 text-white border-0 text-xs">
                {est.implementation_type.replace('_', ' ')}
              </Badge>
            )}
            {results.complexity_label && (
              <Badge className={`${getComplexityColor(results.complexity_label)} text-xs`}>
                {results.complexity_label}
              </Badge>
            )}
            {est.created_at && (
              <span className="flex items-center gap-1 text-slate-400 text-xs">
                <CalendarDays className="w-3.5 h-3.5" />
                {new Date(est.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}
              </span>
            )}
          </div>

          {data.note && (
            <div className="mt-3 p-2.5 rounded-lg bg-white/10 text-sm text-slate-200 italic" data-testid="shared-note">
              "{data.note}"
            </div>
          )}

          {data.expires_at && (
            <div className="mt-2 text-xs text-slate-400 flex items-center gap-1">
              <Clock className="w-3 h-3" />
              Expires: {new Date(data.expires_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
            </div>
          )}
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
        {/* Key Metrics */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="shared-metrics">
          <MetricCard
            icon={Clock}
            label="Total Effort"
            value={`${results.total_effort?.toLocaleString() || 0} hrs`}
            sub={results.effort_range ? `${results.effort_range.low?.toFixed(0)} - ${results.effort_range.high?.toFixed(0)} hrs range` : null}
          />
          <MetricCard
            icon={DollarSign}
            label="Total Cost"
            value={`${sym}${results.total_cost?.toLocaleString(undefined, { maximumFractionDigits: 0 }) || 0}`}
            sub={results.cost_range ? `${sym}${results.cost_range.low?.toLocaleString(undefined, { maximumFractionDigits: 0 })} - ${sym}${results.cost_range.high?.toLocaleString(undefined, { maximumFractionDigits: 0 })}` : null}
            color="#059669"
          />
          <MetricCard
            icon={Target}
            label="Confidence"
            value={`${results.confidence || 0}%`}
            sub={`Score: ${results.complexity_score || 0}/100`}
            color="#6366F1"
          />
          <MetricCard
            icon={Users}
            label="Target Audience"
            value={est.target_audience ? `${est.target_audience.toLocaleString()}` : 'N/A'}
            sub={est.hosting_model ? `${est.hosting_model.replace('_', ' ')}` : null}
            color="#F59E0B"
          />
        </div>

        {/* Auto-projected vs Planner-committed transparency — same line
            shown on Quote PDF / DOCX / XLSX / PPTX cover pages. Gated on
            `include_cost_details` so it only appears on shared links
            that already disclose cost. */}
        {(vis.level === 'full' || vis.include_cost_details) && results.timeline_stretch?.cost > 0 && (
          <Card
            className="border-amber-200 bg-amber-50/40"
            data-testid="shared-timeline-delta"
          >
            <CardContent className="py-2.5 px-3.5 flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
              <div className="text-xs text-[#5C4400] leading-snug">
                <span className="font-semibold">Timeline transparency: </span>
                Auto-projected{' '}
                <span className="font-semibold tabular-nums">
                  ${Math.round(results.total_cost - results.timeline_stretch.cost).toLocaleString()}
                </span>{' '}
                ({results.timeline_stretch.projected_weeks} wk) · Planner-committed{' '}
                <span className="font-semibold tabular-nums">
                  ${Math.round(results.total_cost).toLocaleString()}
                </span>{' '}
                ({results.timeline_stretch.planner_weeks} wk) — added{' '}
                <span className="font-semibold text-amber-700">
                  +${Math.round(results.timeline_stretch.cost).toLocaleString()}
                </span>{' '}
                for the chosen timeline.
              </div>
            </CardContent>
          </Card>
        )}

        {/* Description */}
        {est.implementation_description && (
          <Card>
            <CardContent className="p-5">
              <h3 className="text-sm font-semibold text-[#0F2040] mb-2">Description</h3>
              <p className="text-sm text-slate-600 leading-relaxed">{est.implementation_description}</p>
            </CardContent>
          </Card>
        )}

        {/* Cost & Effort Breakdown */}
        {(vis.level === 'full' || vis.include_cost_details) && (results.effort_by_group || results.cost_by_group) && (
          <div className="grid md:grid-cols-2 gap-4">
            {results.effort_by_group && (
              <Card data-testid="shared-effort-by-group">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm">Effort by Group</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {Object.entries(results.effort_by_group).sort((a, b) => b[1] - a[1]).map(([group, hrs]) => (
                    <div key={group} className="flex items-center justify-between text-sm">
                      <span className="text-slate-600">{group}</span>
                      <span className="font-medium text-[#0F2040]">{hrs.toLocaleString()} hrs</span>
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}
            {results.cost_by_group && (
              <Card data-testid="shared-cost-by-group">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm">Cost by Group</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {Object.entries(results.cost_by_group).sort((a, b) => b[1] - a[1]).map(([group, cost]) => (
                    <div key={group} className="flex items-center justify-between text-sm">
                      <span className="text-slate-600">{group}</span>
                      <span className="font-medium text-[#0F2040]">{sym}{cost.toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* Role Breakdown */}
        {(vis.level === 'full' || vis.include_role_breakdown) && results.effort_by_role && (
          <Card data-testid="shared-role-breakdown">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <BarChart3 className="w-4 h-4 text-[#4ABFCF]" /> Role Allocation
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                {Object.entries(results.effort_by_role).map(([role, hrs]) => (
                  <div key={role} className="p-3 rounded-lg bg-slate-50 text-center">
                    <div className="text-xs text-slate-500 font-medium">{role}</div>
                    <div className="text-lg font-bold text-[#0F2040]">{hrs.toLocaleString()} hrs</div>
                    {results.cost_by_role?.[role] && (
                      <div className="text-xs text-slate-400">{sym}{results.cost_by_role[role].toLocaleString(undefined, { maximumFractionDigits: 0 })}</div>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Component Breakdown */}
        {(vis.level === 'full' || vis.include_components) && Object.keys(groups).length > 0 && (
          <Card data-testid="shared-components">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <Layers className="w-4 h-4 text-[#4ABFCF]" /> Component Breakdown
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {Object.entries(groups).map(([groupName, items]) => {
                const groupHrs = items.reduce((s, c) => s + (c.final_effort || 0), 0);
                const groupCost = items.reduce((s, c) => s + (c.cost || 0), 0);
                const expanded = expandedGroups[groupName];
                return (
                  <div key={groupName} className="border border-slate-100 rounded-lg overflow-hidden" data-testid={`shared-group-${groupName.replace(/\s/g, '-')}`}>
                    <button
                      onClick={() => toggleGroup(groupName)}
                      className="w-full flex items-center justify-between p-3 bg-slate-50/70 hover:bg-slate-50 transition-colors"
                    >
                      <div className="flex items-center gap-2">
                        {expanded ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
                        <span className="text-sm font-medium text-[#0F2040]">{groupName}</span>
                        <Badge variant="secondary" className="text-[10px]">{items.length}</Badge>
                      </div>
                      <div className="flex gap-4 text-xs text-slate-500">
                        <span>{groupHrs.toFixed(1)} hrs</span>
                        <span>{sym}{groupCost.toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>
                      </div>
                    </button>
                    {expanded && (
                      <div className="divide-y divide-slate-50">
                        {items.map((comp, idx) => (
                          <div key={`${comp.name}-${comp.role}-${idx}`} className="flex items-center justify-between px-4 py-2 text-xs">
                            <div className="flex items-center gap-2">
                              <span className="text-slate-700">{comp.name}</span>
                              <Badge variant="outline" className="text-[9px]">{comp.role}</Badge>
                              {comp.volume_based && <Badge className="bg-violet-50 text-violet-600 text-[9px]">Volume</Badge>}
                            </div>
                            <div className="flex gap-4 text-slate-500">
                              <span>{comp.final_effort} hrs</span>
                              <span>{sym}{comp.cost?.toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </CardContent>
          </Card>
        )}

        {/* Modifiers */}
        {(vis.level === 'full' || vis.include_modifiers) && est.modifiers && (
          <Card data-testid="shared-modifiers">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">Project Modifiers</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {Object.entries(est.modifiers).map(([key, val]) => (
                  <div key={key} className="p-2.5 rounded-lg bg-slate-50">
                    <div className="text-xs text-slate-500 capitalize">{key.replace(/_/g, ' ')}</div>
                    <div className="text-sm font-semibold text-[#0F2040]">{(val * 100).toFixed(0)}%</div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Risks & Suggestions */}
        {(results.risks?.length > 0 || results.suggestions?.length > 0) && (
          <div className="grid md:grid-cols-2 gap-4">
            {results.risks?.length > 0 && (
              <Card data-testid="shared-risks">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4 text-amber-500" /> Risks
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {results.risks.map((r, i) => (
                    <div key={`risk-${r.type}-${i}`} title={r.detail || undefined} className={`flex items-start gap-2 text-sm ${r.detail ? 'cursor-help' : ''}`}>
                      <Badge className={`text-[10px] shrink-0 ${r.type === 'high' ? 'bg-red-100 text-red-600' : 'bg-amber-100 text-amber-600'}`}>{r.type}</Badge>
                      <span className="text-slate-600">{r.message}</span>
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}
            {results.suggestions?.length > 0 && (
              <Card data-testid="shared-suggestions">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <Lightbulb className="w-4 h-4 text-blue-500" /> Suggestions
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {results.suggestions.map((s, i) => (
                    <div key={`sug-${i}`} className="text-sm text-slate-600">
                      {s.message}
                      {s.potential_savings > 0 && (
                        <Badge className="ml-2 bg-green-50 text-green-600 text-[10px]">Save ~${s.potential_savings.toLocaleString()}</Badge>
                      )}
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* Overhead */}
        {results.overhead && (
          <Card data-testid="shared-overhead">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">Overhead (PM & Testing)</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                <div className="p-2.5 rounded-lg bg-slate-50">
                  <div className="text-xs text-slate-500">PM Hours</div>
                  <div className="font-semibold text-[#0F2040]">{results.overhead.pm_hours?.toFixed(1)} hrs</div>
                </div>
                <div className="p-2.5 rounded-lg bg-slate-50">
                  <div className="text-xs text-slate-500">PM Cost</div>
                  <div className="font-semibold text-[#0F2040]">{sym}{results.overhead.pm_cost?.toLocaleString(undefined, { maximumFractionDigits: 0 })}</div>
                </div>
                <div className="p-2.5 rounded-lg bg-slate-50">
                  <div className="text-xs text-slate-500">Testing Hours</div>
                  <div className="font-semibold text-[#0F2040]">{results.overhead.testing_hours?.toFixed(1)} hrs</div>
                </div>
                <div className="p-2.5 rounded-lg bg-slate-50">
                  <div className="text-xs text-slate-500">Testing Cost</div>
                  <div className="font-semibold text-[#0F2040]">{sym}{results.overhead.testing_cost?.toLocaleString(undefined, { maximumFractionDigits: 0 })}</div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Footer */}
        <div className="text-center py-6 border-t border-slate-200 mt-8">
          <p className="text-xs text-slate-400 inline-flex items-center gap-1.5" data-testid="shared-footer-powered-by">
            <img
              src="/seertech-mark.png"
              alt=""
              aria-hidden="true"
              className="w-3.5 h-3.5 opacity-60"
            />
            Powered by <span className="font-semibold text-[#0F2040]">Seertech Solutions</span> Seertech Blueprint Studio
          </p>
          <p className="text-[10px] text-slate-300 mt-1">This is a read-only shared view. Contact the sender for questions.</p>
        </div>
      </div>
    </div>
  );
}
